// src/services/busActivityService.js
// Wrapper around the websocket service's REST + WS endpoints for the planning page.

const WS_REST_BASE = "https://ws-prd.enlaparadave.com/v1/websocket";
const WS_SOCKET_BASE = "wss://ws-prd.enlaparadave.com/employee-native-websocket";

// Shared POST helper for the two activity endpoints — they take the same shape
// (busIds in body + optional startDate/endDate query params).
const postBusIdsWithRange = async (token, path, busIds, startDate, endDate, label) => {
  if (!Array.isArray(busIds) || busIds.length === 0) return [];
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const qs = params.toString();
    const url = `${WS_REST_BASE}${path}${qs ? `?${qs}` : ""}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(busIds),
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${path}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`❌ Error en ${label}:`, error);
    return [];
  }
};

/**
 * Fetches DailyActivity entries (rebuilt nightly from BusDriverData logs) for
 * the given busIds within the date range. Used for past-day cells.
 * Returns: [{ dailyActivityId, busId, activityDate, workShifts: [...] }, ...].
 */
export const getBusActivityByBusIds = (token, busIds, startDate, endDate) =>
  postBusIdsWithRange(token, "/reports/bus-activity/by-bus-ids", busIds, startDate, endDate, "getBusActivityByBusIds");

/**
 * Fetches RealtimeBusArrival entries (one per bus per day, recorded the moment
 * a bus enters its route's startStop or endStop). Lets us paint today's column
 * without waiting for the 3 AM batch.
 * Returns: [{ realtimeBusArrivalId, busId, rouId, arrivalDate, firstArrivalAt, stopType }, ...].
 */
export const getRealtimeArrivalsByBusIds = (token, busIds, startDate, endDate) =>
  postBusIdsWithRange(token, "/reports/realtime-arrivals/by-bus-ids", busIds, startDate, endDate, "getRealtimeArrivalsByBusIds");

/**
 * Opens a websocket subscription to /employee-native-websocket/bus-activity.
 * onArrival is invoked with each BUS_ARRIVAL message; onStatusChange reports connection state.
 *
 * Returns a disposer that closes the socket and stops the auto-reconnect loop.
 * Auto-reconnects on close as long as the disposer hasn't been called.
 */
export const subscribeBusActivity = (token, { onArrival, onStatusChange } = {}) => {
  if (!token) return () => {};

  let ws = null;
  let retryTimeout = null;
  let disposed = false;

  const connect = () => {
    if (disposed) return;
    const url = `${WS_SOCKET_BASE}/bus-activity?token=${token}`;
    ws = new WebSocket(url);

    ws.onopen = () => {
      if (onStatusChange) onStatusChange("open");
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && data.type === "BUS_ARRIVAL" && onArrival) {
          onArrival(data);
        }
      } catch (e) {
        console.warn("[bus-activity] payload no JSON:", event.data);
      }
    };

    ws.onerror = () => {
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        try { ws.close(); } catch (e) { /* ignore */ }
      }
    };

    ws.onclose = () => {
      if (onStatusChange) onStatusChange("closed");
      ws = null;
      if (!disposed) {
        retryTimeout = setTimeout(connect, 3000);
      }
    };
  };

  connect();

  return () => {
    disposed = true;
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
    if (ws) {
      ws.onclose = null;
      try { ws.close(); } catch (e) { /* ignore */ }
      ws = null;
    }
  };
};
